import { Switch } from "react-router-dom";
import { Redirect, Route } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import "./App.css";
import About from "./components/About/About";
import Header from "./components/Header/Header/Header";
import Home from "./components/Home/Home";
import { ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import Notfound from "./components/Notfound/Notfound";
import Footer from "./components/Header/Footer/Footer";
import Authprovider from "./Context/Authprovider";
import Services from "./components/Services/Services";
import Appointment from "./components/Services/Appointment/Appointment";
import ServiceDetails from "./components/Services/ServiceDetails/ServiceDetails";
import ServiceRedirect from "./components/Services/ServiceRedirect";
import ArtistInventory from "./components/Services/ServiceContent/ArtistInventory";
import SecretCode from "./components/SecretCode/SecretCode";
import { getSeasonalMode } from "./utils/seasonalMode";
import ScrollToTop from "./utils/ScrollToTop";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Lazy: only reached from an email link or after Stripe, and the vendor area
// pulls in the date pickers — none of it belongs in the main bundle.
const PopupConfirmed = lazy(() => import("./components/PopUps/PopupConfirmed"));
const VendorArea = lazy(() => import("./components/PopUps/vendor/VendorArea"));
const OrderArea = lazy(() => import("./components/Order/OrderArea"));

// custom style for this application
export const myTheme = createTheme({
  palette: {
    primary: {
      main: "#0f7ca4",
    },
    secondary: {
      main: "#99cced",
    },
    alternate: {
      main: "#fff",
    },
    text: {
      secondary: "#212121",
    },
  },
  typography: {
    fontFamily: "Quicksand",
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
  },
});

function GoogleAdsPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
}


// The ordering pages are what a customer sees after scanning a pop-up's QR
// code at their table. DenDen's nav and footer belong to the marketing site and
// only get in the way there — on a phone the footer is a screen of links
// underneath the menu they're trying to order from.
const SiteChrome = ({ children }) => {
  const { pathname } = useLocation();
  return pathname.startsWith("/order/") ? null : children;
};

function App() {
  const seasonalMode = getSeasonalMode();
  const buildEmojiShower = (emojis, count) =>
    Array.from({ length: count }, (_, index) => {
      const size = 12 + Math.random() * 16;
      const duration = 10 + Math.random() * 12;
      const delay = -Math.random() * duration;

      return {
        id: index,
        left: Math.random() * 100,
        size,
        duration,
        delay,
        drift: (Math.random() * 20 - 10).toFixed(2),
        opacity: (0.5 + Math.random() * 0.45).toFixed(2),
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
      };
    });

  const valentinesHearts = useMemo(
    () => buildEmojiShower(["\u{1F496}", "\u{1F497}", "\u{1F495}"], 36),
    []
  );

  const lunarEmojis = useMemo(
    () =>
      buildEmojiShower(
        ["\u{1F9E7}", "\u{1F3EE}", "\u2728", "\u{1F9E7}", "\u{1F40E}"],
        32
      ),
    []
  );

  return (
    <Authprovider>
       <HelmetProvider>
      <ThemeProvider theme={myTheme}>
        <div className={seasonalMode ? `${seasonalMode}-mode` : ""}>
          {seasonalMode === "halloween" && (
            <>
              <div className="web top-left" />
              <div className="web top-right" />
            </>
          )}
          {seasonalMode === "valentines" && (
            <div className="valentines-shower" aria-hidden="true">
              {valentinesHearts.map((heart) => (
                <span
                  key={heart.id}
                  className="valentines-heart"
                  style={{
                    left: `${heart.left}%`,
                    fontSize: `${heart.size}px`,
                    opacity: heart.opacity,
                    animationDuration: `${heart.duration}s`,
                    animationDelay: `${heart.delay}s`,
                    "--drift": `${heart.drift}vw`,
                  }}
                >
                  {heart.emoji}
                </span>
              ))}
            </div>
          )}
          {seasonalMode === "lunar" && (
            <div className="lunar-shower" aria-hidden="true">
              {lunarEmojis.map((item) => (
                <span
                  key={item.id}
                  className="lunar-emoji"
                  style={{
                    left: `${item.left}%`,
                    fontSize: `${item.size}px`,
                    opacity: item.opacity,
                    animationDuration: `${item.duration}s`,
                    animationDelay: `${item.delay}s`,
                    "--drift": `${item.drift}vw`,
                  }}
                >
                  {item.emoji}
                </span>
              ))}
            </div>
          )}

          <BrowserRouter>
            {/* ✅ MOVE TRACKER HERE */}
            <GoogleAdsPageViewTracker />

            <ScrollToTop />
            <SiteChrome><Header /></SiteChrome>

            <Suspense
              fallback={
                <Box sx={{ textAlign: "center", py: 10 }}>
                  <CircularProgress />
                </Box>
              }
            >
            <Switch>
              <Route exact path="/" component={Home} />
              <Route exact path="/home" render={() => <Redirect to="/" />} />
              <Route path="/about" component={About} />
              <Route exact path="/contact" component={Appointment} />
              <Route
                exact
                path="/appointment"
                render={() => <Redirect to="/contact" />}
              />
              <Route exact path="/services" component={Services} />
              <Route
                exact
                path="/explore/artist-alley/:artistName"
                component={ArtistInventory}
              />
              <Route exact path="/secret-code" component={SecretCode} />
              {/* Non-exact: VendorArea owns its own nested <Switch>. */}
              <Route path="/explore/pop-ups/vendor" component={VendorArea} />
              {/* What the pop-up's QR codes point at. Also non-exact. */}
              <Route path="/order/:qrSlug" component={OrderArea} />
              <Route
                exact
                path="/explore/pop-ups/confirmed"
                component={PopupConfirmed}
              />
              <Route exact path="/explore/:slug" component={ServiceDetails} />
              <Route
                exact
                path="/services/details/:servId"
                component={ServiceRedirect}
              />
              <Route path="*" component={Notfound} />
            </Switch>
            </Suspense>

            <SiteChrome><Footer /></SiteChrome>
          </BrowserRouter>

        </div>
      </ThemeProvider>
       </HelmetProvider>
    </Authprovider>
  );
}


export default App;
